import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShoppingBag, ArrowDown, Loader2, ShieldCheck, Leaf } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

const MaskedLines = ({ lines }) => (
  <h1 className="font-display text-[2.35rem] sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-[#143C18] leading-[1.02] tracking-tight" data-testid="landing-hero-title">
    {lines.map((l, i) => (
      <span key={l} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
        <motion.span
          className={`block ${i === lines.length - 1 ? "italic font-serif-alt text-[#2E7D32]" : ""}`}
          initial={{ y: "112%" }}
          animate={{ y: 0 }}
          transition={{ duration: 1, ease, delay: 0.2 + i * 0.13 }}
        >
          {l}
        </motion.span>
      </span>
    ))}
  </h1>
);

export const LandingHero = ({ config, shop }) => {
  const { hero } = config;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "6%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="home" className="relative pt-[76px] sm:pt-28 lg:pt-32 pb-12 lg:pb-20 lg:min-h-[100svh] overflow-hidden bg-hero-gradient grain" data-testid="landing-hero">
      <div className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full opacity-40 blur-3xl pointer-events-none" style={{ background: `radial-gradient(circle, ${hero.accent}55, transparent 70%)` }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-8 items-center">
          <motion.div style={{ y: textY, opacity: fade }} className="lg:col-span-7 relative z-10 order-2 lg:order-1">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }} className="chip chip-gold mb-6">
              <Leaf className="w-3.5 h-3.5" /> {hero.kicker}
            </motion.div>
            <MaskedLines lines={hero.lines} />
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }} className="mt-6 text-base sm:text-lg text-[#5D4037] max-w-lg leading-relaxed" data-testid="landing-hero-sub">
              {hero.sub}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.85 }} className="mt-7 grid grid-cols-1 sm:flex sm:flex-wrap gap-3">
              <button onClick={() => shop.buyNow(1)} disabled={shop.buying || !shop.available} data-testid="landing-hero-buy-btn" className="btn-primary !py-4 sm:!py-3.5 justify-center text-base disabled:opacity-60">
                {shop.buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
                {hero.ctaLabel} – {shop.priceLabel}
              </button>
              <a href={hero.secondary.href} data-testid="landing-hero-secondary-btn" className="btn-outline !py-3.5 justify-center">
                {hero.secondary.label} <ArrowDown className="w-4 h-4" />
              </a>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.1 }} className="mt-8 flex items-center gap-2 text-sm text-[#5D4037]">
              <ShieldCheck className="w-4 h-4 text-[#2E7D32]" /> Secure Shopify checkout · Ships across India
            </motion.div>
          </motion.div>

          <div className="lg:col-span-5 relative order-1 lg:order-2">
            <motion.div
              initial={{ clipPath: "inset(12% 18% 12% 18% round 999px 999px 32px 32px)", opacity: 0 }}
              animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 32px 32px)", opacity: 1 }}
              transition={{ duration: 1.4, ease, delay: 0.35 }}
              className="relative mx-auto w-[64%] sm:w-[56%] lg:w-[82%] max-w-[460px] aspect-[3/4] overflow-hidden shadow-[0_50px_90px_-30px_rgba(20,60,24,0.45)] arch-frame"
              data-testid="landing-hero-image-frame"
            >
              <motion.img
                src={hero.image}
                alt={hero.imageAlt}
                style={{ y: imgY, scale: 1.06, objectPosition: "50% 45%" }}
                width="1254"
                height="1254"
                className="w-full h-full object-cover will-change-transform"
                loading="eager"
                fetchPriority="high"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#143C18]/15 via-transparent to-transparent pointer-events-none" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8, rotate: -6 }}
              animate={{ opacity: 1, scale: 1, rotate: -6 }}
              transition={{ duration: 0.7, delay: 1.2, type: "spring" }}
              className="absolute top-6 right-2 sm:right-8 lg:right-0 bg-[#143C18] text-white rounded-2xl px-4 py-3 shadow-xl"
              data-testid="landing-hero-price-badge"
            >
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#E8BE4A] font-bold">{config.weight} Jar</div>
              <div className="font-display text-2xl font-bold leading-none mt-0.5">{shop.priceLabel}</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.35 }}
              className="absolute -bottom-3 left-2 sm:left-10 lg:left-4 bg-white rounded-2xl px-4 py-3 shadow-xl border border-[#D4A017]/30 flex items-center gap-3"
            >
              <span className={`w-2.5 h-2.5 rounded-full ${shop.available ? "bg-[#2E7D32] animate-pulse" : "bg-[#8D6E63]"}`} />
              <span className="text-sm font-bold text-[#143C18]">{shop.loading ? "Checking stock…" : shop.available ? "In stock · Ready to ship" : "Out of stock"}</span>
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }} className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-[#8D6E63]">
        <span className="text-[10px] uppercase tracking-[0.3em] font-bold">Scroll</span>
        <span className="w-px h-10 bg-gradient-to-b from-[#8D6E63] to-transparent scroll-line" />
      </motion.div>
    </section>
  );
};
